import { Alert } from '@risksmart-app/atomic-ui';
import { User01, Users01 } from '@untitled-ui/icons-react';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ControlledMultiselect from 'src/components/form/controlled-multiselect';
import type { HidableOptionGroup } from 'src/components/form/controlled-multiselect/types';
import type { StatusType } from 'src/components/form/controlled-select/ControlledSelect';
import { FormField } from 'src/components/form/form/FormField';

import type { ScheduleFormFieldData } from './scheduleFormSchema';
import type { AssigneeOption, ScheduleTarget } from './scheduleTargets';

type Props = {
  target: ScheduleTarget;
  users: AssigneeOption[];
  groups: AssigneeOption[];
  statusType: StatusType;
};

/**
 * Options come from the target's own pool rather than the shared user picker:
 * the server validates every assignee against that one object's direct owners
 * and contributors, so anything the shared picker offers on top of that is a
 * value the save would reject.
 */
export const AssigneeField: FC<Props> = ({
  target,
  users,
  groups,
  statusType,
}) => {
  const { t } = useTranslation(['common']);
  const { control } = useFormContext<ScheduleFormFieldData>();
  const name = `AssigneesByTarget.${target.value}` as const;
  const { error } = control.getFieldState(name);
  const label = t('scheduler.fields.assignees', { target: target.label });

  const options = useMemo(() => {
    const grouped: HidableOptionGroup[] = [];
    if (users.length > 0) {
      grouped.push({
        label: t('users'),
        options: users.map((user) => ({
          ...user,
          iconSvg: <User01 viewBox={'0 0 28 28'} width={28} height={28} />,
        })),
      });
    }
    if (groups.length > 0) {
      grouped.push({
        label: t('groups'),
        options: groups.map((group) => ({
          ...group,
          iconSvg: <Users01 viewBox={'0 0 28 28'} width={28} height={28} />,
        })),
      });
    }

    return grouped;
  }, [users, groups, t]);

  // Only once the pool is known: an empty one mid-read is not an empty one, and
  // the field must stay on screen either way — the schema requires an assignee
  // for every target, and an error on a field nobody can see cannot be resolved.
  if (options.length === 0 && statusType === 'finished') {
    return (
      <FormField label={label} errorText={error?.message} stretch={true}>
        <Alert variant={'warning'} size={'sm'}>
          <Alert.Info />
          <Alert.Description>
            {t('scheduler.fields.assignees_empty_pool', {
              target: target.label,
            })}
          </Alert.Description>
        </Alert>
      </FormField>
    );
  }

  return (
    <ControlledMultiselect
      control={control}
      name={name}
      label={label}
      options={options}
      statusType={statusType}
      testId={`schedule-assignees-${target.value}`}
    />
  );
};
